import React, { useState, useEffect } from "react";
import "../pages/css/DashBoard.css";
import Header from "../pages/components/HeaderUser";
import SideBar from "../pages/components/SidebarUser";
import UserVerificationUser from "../pages/components/UserVerificationUser";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, get, update } from "firebase/database";
import { getAuth } from "firebase/auth";
import Swal from 'sweetalert2';

function AddCoins() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [walletBalance, setWalletBalance] = useState(null); // null = not loaded yet
    const [loading, setLoading] = useState(true);
    const [prices, setPrices] = useState({ bitcoin: null, ethereum: null, tether: null });
    const [selectedCoin, setSelectedCoin] = useState("bitcoin");
    const [inputType, setInputType] = useState("coin"); // coin | usd | inr
    const [inputValue, setInputValue] = useState("");
    const [calculated, setCalculated] = useState(null);

    const auth = getAuth();
    const db = getDatabase();
    const navigate = useNavigate();

    UserVerificationUser();

    // Fetch wallet balance
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                const uid = user.uid;
                get(ref(db, `blackstone-users/${uid}/walletBalance`))
                    .then((snapshot) => {
                        if (snapshot.exists()) {
                            const balance = Number(snapshot.val()) || 0;
                            console.log("Fetched walletBalance from Firebase:", snapshot.val());
                            console.log("Parsed walletBalance (Number):", balance);
                            setWalletBalance(balance); // ensure numeric
                        } else {
                            console.log("No walletBalance found, setting to 0");
                            setWalletBalance(0);
                        }
                    })
                    .catch((err) => {
                        console.error("Error fetching walletBalance:", err);
                        setWalletBalance(0);
                    });
            } else {
                console.log("User not logged in yet");
                setWalletBalance(0);
            }
        });

        return () => unsubscribe(); // cleanup
    }, []);



    // Fetch live prices
    const fetchPrices = async () => {
        try {
            const ids = "bitcoin,ethereum,tether";
            const resp = await fetch(
                `https://api.coingecko.com/api/v3/simple/price?ids=${ids}&vs_currencies=usd,inr`
            );
            const data = await resp.json();
            setPrices({
                bitcoin: data.bitcoin,
                ethereum: data.ethereum,
                tether: data.tether,
            });
        } catch (err) {
            console.error("Error fetching prices", err);
        }
    };

    useEffect(() => {
        fetchPrices();
        const interval = setInterval(fetchPrices, 15000); // refresh every 15 sec
        return () => clearInterval(interval);
    }, []);

    // Recalculate when user changes input
    useEffect(() => {
        if (!inputValue || !prices[selectedCoin]) {
            setCalculated(null);
            return;
        }

        const { usd, inr } = prices[selectedCoin];
        let result = {};

        if (inputType === "coin") {
            result = {
                coins: parseFloat(inputValue),
                usd: parseFloat(inputValue) * usd,
                inr: parseFloat(inputValue) * inr,
            };
        } else if (inputType === "usd") {
            result = {
                coins: parseFloat(inputValue) / usd,
                usd: parseFloat(inputValue),
                inr: (parseFloat(inputValue) / usd) * inr,
            };
        } else if (inputType === "inr") {
            result = {
                coins: parseFloat(inputValue) / inr,
                usd: (parseFloat(inputValue) / inr) * usd,
                inr: parseFloat(inputValue),
            };
        }

        setCalculated(result);
    }, [inputValue, inputType, selectedCoin, prices]);

    const handleBuyNow = async () => {
        if (!calculated) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: 'Please enter an amount to buy.',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true
            });
            return;
        }

        const inrAmount = calculated.inr;
        if (inrAmount > walletBalance) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Insufficient wallet balance!',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true
            });
            return;
        }

        const uid = auth.currentUser.uid;
        const coinName = selectedCoin;
        const coinAmount = calculated.coins;

        const userWalletRef = ref(db, `blackstone-users/${uid}`);

        try {
            // 1️⃣ Get current wallet balance and coins
            const snapshot = await get(userWalletRef);
            if (!snapshot.exists()) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: 'User data not found.',
                    showConfirmButton: false,
                    timer: 2500,
                    timerProgressBar: true
                });
                return;
            }

            const userData = snapshot.val();
            const currentWallet = Number(userData.walletBalance) || 0;
            const currentCoins = userData.buyedCoins || {};

            // 2️⃣ Update wallet balance
            const newWalletBalance = currentWallet - inrAmount;

            // 3️⃣ Update coin balance cumulatively
            const existingCoinAmount = currentCoins[coinName] || 0;
            const updatedCoins = {
                ...currentCoins,
                [coinName]: existingCoinAmount + coinAmount
            };

            // 4️⃣ Write updated data to database
            await update(userWalletRef, {
                walletBalance: newWalletBalance,
                buyedCoins: updatedCoins
            });

            // 5️⃣ Update local state
            setWalletBalance(newWalletBalance);

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: `Successfully bought ${coinAmount.toFixed(6)} ${coinName.toUpperCase()}!`,
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true
            });

            // Optional: reset input
            setInputValue("");
            setCalculated(null);

        } catch (err) {
            console.error("Error buying coins:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: 'Something went wrong. Please try again.',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true
            });
        }
    };


    return (
        <>
            <div className="d-flex layout-container">
                <SideBar isOpen={isSidebarOpen} />

                <div className="flex-grow-1">
                    <Header onMenuClick={() => setIsSidebarOpen((prev) => !prev)} />
                    <div className="scrollable-section p-4">
                        {walletBalance >= 200 ? (
                            <div className="card p-4 shadow-lg" style={{ borderRadius: "15px" }}>
                                <h4 className="mb-3">Buy Coins</h4>
                                <p className="alert alert-success d-flex justify-content-between align-items-center">
                                    <strong>Wallet Balance:</strong> ₹{walletBalance.toLocaleString("en-IN")}
                                </p>

                                {/* Coin Selection */}
                                <div className="mb-3">
                                    <label className="form-label">Select Coin</label>
                                    <select
                                        className="form-select"
                                        value={selectedCoin}
                                        onChange={(e) => setSelectedCoin(e.target.value)}
                                    >
                                        <option value="bitcoin">Bitcoin (BTC)</option>
                                        <option value="ethereum">Ethereum (ETH)</option>
                                        <option value="tether">Tether (USDT)</option>
                                    </select>
                                </div>

                                {/* Input Type */}
                                <div className="mb-3">
                                    <label className="form-label">Enter Type</label>
                                    <select
                                        className="form-select"
                                        value={inputType}
                                        onChange={(e) => setInputType(e.target.value)}
                                    >
                                        <option value="coin">Coin Amount</option>
                                        <option value="usd">USD Amount</option>
                                        <option value="inr">INR Amount</option>
                                    </select>
                                </div>

                                {/* Input Value */}
                                <div className="mb-3">
                                    <label className="form-label">
                                        Enter {inputType === "coin" ? "Coin Amount" : inputType.toUpperCase()}
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                    />
                                </div>

                                {/* Results */}
                                {calculated && (
                                    <div className="alert alert-info">
                                        <p>
                                            <strong>Coins:</strong> {calculated.coins.toFixed(6)}
                                        </p>
                                        <p>
                                            <strong>USD:</strong> ${calculated.usd.toLocaleString()}
                                        </p>
                                        <p>
                                            <strong>INR:</strong> ₹{calculated.inr.toLocaleString("en-IN")}
                                        </p>
                                    </div>
                                )}

                                <button className="btn btn-success w-100" onClick={handleBuyNow}>
                                    Proceed to Buy
                                </button>

                            </div>
                        ) : (
                            <div className="alert alert-warning d-flex justify-content-between align-items-center">
                                <span>
                                    Your wallet balance is insufficient to buy coins. Please add funds first.
                                </span>
                                <button
                                    className="btn btn-sm btn-primary ms-3"
                                    onClick={() => navigate("/add-funds")}
                                >
                                    Recharge Wallet
                                </button>
                            </div>

                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default AddCoins;
